import { Grid, styled, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { setNewMortgageAction } from "../../../../contexts/mortgage/Actions";
import { useMortgageDispatch, useMortgageState } from "../../../../contexts/mortgage/Reducer";
import { useFinanceCalculatorService } from "../../../../services/finance-calculator/FinanceCalculatorServiceContext";
import { formatAmount, parseNumberWithLocale } from "../../../../utils/NumberUtils";
import ErrorMessage from "../../../feedback/ErrorMessage";
import ConfiguratorInnerBox from "./ConfiguratorInnerBox";
import MortgageSlider from "./MortageSlider";

const Amount = styled(TextField)(({ theme }) => ({
    width: "100%",
    marginBottom: "15px",
    marginTop: "20px",
    [theme.breakpoints.up("md")]: {
        marginTop: "5px",
        marginBottom: "30px",
    },
    [theme.breakpoints.up("lg")]: {
        marginTop: "56px",
        marginBottom: "42px",
    },
}));

export const MIN_AMOUNT_NEW_MORTGAGE = 100000;
export const STEP_VALUE = 5000;

const PaymentConfigurator = () => {
    const { t } = useTranslation();
    const { mortgage, newMortgage } = useMortgageState();
    const mortgageDispatch = useMortgageDispatch();
    const [inputAmountString, setInputAmountString] = useState<string>(formatAmount(newMortgage?.amount ?? 0));
    const financeCalculatorService = useFinanceCalculatorService();

    useEffect(() => {
        if (newMortgage) {
            setInputAmountString(formatAmount(newMortgage.amount));
        }
    }, [newMortgage]);

    if (newMortgage === undefined || mortgage === undefined) {
        return <ErrorMessage />;
    }

    const maximumAmountOfNewMortgage = mortgage.amountMaturity; // DCI-1578: "For the extension, however, only the amount at maturity may be displayed"

    const onSliderChange = (newValue: number) => {
        const newMortgageCopy = { ...newMortgage };
        financeCalculatorService.onAmountChange(newValue, newMortgageCopy, maximumAmountOfNewMortgage);
        mortgageDispatch(setNewMortgageAction(newMortgageCopy));
    };

    const amountEditConfirm = () => {
        const number = parseNumberWithLocale(inputAmountString);
        if (Number.isNaN(number)) {
            onSliderChange(maximumAmountOfNewMortgage);
            return;
        }
        const amount = Math.ceil(number / STEP_VALUE) * STEP_VALUE;
        if (amount < MIN_AMOUNT_NEW_MORTGAGE) {
            onSliderChange(MIN_AMOUNT_NEW_MORTGAGE);
        } else if (amount > maximumAmountOfNewMortgage) {
            onSliderChange(maximumAmountOfNewMortgage);
        } else {
            onSliderChange(amount);
        }
    };

    function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
        setInputAmountString(event.target.value);
    }

    const onAmountEditEnterPress = (event: any) => {
        if (event.key === "Enter") {
            // just lose focus, rest will be taken care of
            event.target.blur();
        }
    };

    return (
        <Grid item sm={12} lg={3.7}>
            <ConfiguratorInnerBox>
                {
                    <Amount
                        label={t("General.Amount in", { currency: newMortgage.currency })}
                        value={inputAmountString}
                        onKeyDown={onAmountEditEnterPress}
                        onChange={handleInputChange}
                        onBlur={amountEditConfirm}
                        data-cy={"payment-amount"}
                        inputProps={{
                            sx: {
                                fontFamily: "Yoga Sans",
                                fontWeight: "400",
                                fontSize: "16px",
                            },
                        }}
                    />
                }
                <MortgageSlider
                    min={MIN_AMOUNT_NEW_MORTGAGE}
                    max={maximumAmountOfNewMortgage}
                    onSliderChange={onSliderChange}
                    sliderStep={STEP_VALUE}
                    value={newMortgage.amount}
                    selector={"payment-slider"}
                />
            </ConfiguratorInnerBox>
        </Grid>
    );
};

export default PaymentConfigurator;
